// Texting: types mirror crates/api-types/src/messaging.rs (the console keeps its own API layer).
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "./client";

export interface MsgUserRef {
  id: string;
  name: string;
  email?: string | null;
}

export type MessageDirection = "inbound" | "outbound";
export type MessageStatus = "queued" | "sending" | "sent" | "delivered" | "failed" | "received";
export type MessageKind = "message" | "auto_reply" | "event";
export type TextingState = "not_registered" | "pending" | "approved" | "rejected" | "unknown";

export interface MessageMedia {
  id: string;
  content_type: string;
  size_bytes: number | null;
  url: string | null;
}

export interface SmsMessage {
  id: string;
  conversation_id: string;
  direction: MessageDirection;
  kind: MessageKind;
  body: string;
  media: MessageMedia[];
  status: MessageStatus;
  error_code: string | null;
  error_message: string | null;
  segments: number;
  encoding: "gsm7" | "ucs2";
  sent_by: MsgUserRef | null;
  created_at: string;
  sent_at: string | null;
  delivered_at: string | null;
  retryable: boolean;
}

export interface SmsConversation {
  id: string;
  our_number: string;
  contact_number: string;
  contact: {
    ref: { model: string; id: string } | null;
    name: string;
    company: string | null;
    web_url: string | null;
  } | null;
  brand: string | null;
  assigned_to: MsgUserRef | null;
  unread_count: number;
  last_message_at: string | null;
  last_message_preview: string | null;
  last_direction: MessageDirection | null;
  opted_out: boolean;
  texting_state: TextingState;
}

export interface ConversationList {
  items: SmsConversation[];
  next_cursor: string | null;
  unread_total: number;
}

export interface MessagePage {
  items: SmsMessage[];
  next_before: string | null;
}

export interface SmsTemplate {
  id: string;
  name: string;
  body: string;
  scope: "tenant" | "personal";
  editable: boolean;
  updated_at: string;
}

export interface TemplateList {
  items: SmsTemplate[];
  variables: string[];
}

export interface NumberTexting {
  e164: string;
  brand: string | null;
  owner: MsgUserRef | null;
  state: TextingState;
  campaign_id: string | null;
  campaign_status: string | null;
  detail: string | null;
  checked_at: string | null;
}

export interface TextingStatus {
  overall: TextingState;
  numbers: NumberTexting[];
}

export type CrmLogMode = "each" | "daily_digest" | "off";
export type QuietHoursMode = "policy" | "custom" | "off";

export interface TextingSettings {
  help_text: string;
  auto_reply_help: boolean;
  crm_log_mode: CrmLogMode;
  quiet_hours_mode: QuietHoursMode;
  quiet_hours: { start: string; end: string } | null;
  max_media_bytes: number;
}

export interface TextingAdmin {
  settings: TextingSettings;
  status: TextingStatus;
  opt_out_count: number;
}

export type OptOutSource = "keyword" | "carrier" | "admin";

export interface OptOut {
  e164: string;
  contact_name: string | null;
  opted_out_at: string;
  source: OptOutSource;
  keyword: string | null;
  our_number: string | null;
  removable: boolean;
}

export interface OptOutList {
  items: OptOut[];
  total: number;
}

export interface TextingUsageRow {
  user_id: string | null;
  user_name: string;
  sent: number;
  received: number;
  segments_sent: number;
  segments_received: number;
  mms: number;
  failed: number;
  est_cost_usd: number;
}

export interface TextingUsage {
  from: string;
  to: string;
  rows: TextingUsageRow[];
}

export interface ConversationQuery {
  filter?: "all" | "unread" | undefined;
  q?: string | undefined;
  user_id?: string | undefined;
}

export const messaging = {
  conversations: (q: ConversationQuery & { cursor?: string | undefined }, signal?: AbortSignal) =>
    api<ConversationList>("/v1/admin/messages/conversations", { query: { ...q, limit: 50 }, signal }),
  conversation: (id: string) =>
    api<SmsConversation>(`/v1/admin/messages/conversations/${encodeURIComponent(id)}`),
  thread: (id: string, before?: string) =>
    api<MessagePage>(`/v1/admin/messages/conversations/${encodeURIComponent(id)}/messages`, {
      query: { before, limit: 200 },
    }),
  texting: () => api<TextingAdmin>("/v1/admin/texting"),
  putTexting: (body: TextingSettings) => api<TextingAdmin>("/v1/admin/texting", { method: "PUT", body }),
  refresh: () => api<TextingStatus>("/v1/admin/texting/refresh", { method: "POST" }),
  optOuts: (q: { q?: string | undefined }) => api<OptOutList>("/v1/admin/texting/opt_outs", { query: q }),
  addOptOut: (e164: string) => api<void>("/v1/admin/texting/opt_outs", { method: "POST", body: { e164 } }),
  removeOptOut: (e164: string) =>
    api<void>(`/v1/admin/texting/opt_outs/${encodeURIComponent(e164)}`, { method: "DELETE" }),
  usage: (q: { from: string; to: string }) => api<TextingUsage>("/v1/admin/texting/usage", { query: q }),
  templates: () => api<TemplateList>("/v1/messages/templates"),
  createTemplate: (body: { name: string; body: string }) =>
    api<SmsTemplate>("/v1/messages/templates", { method: "POST", body: { ...body, scope: "tenant" } }),
  updateTemplate: (id: string, body: { name: string; body: string }) =>
    api<SmsTemplate>(`/v1/messages/templates/${encodeURIComponent(id)}`, { method: "PATCH", body }),
  deleteTemplate: (id: string) =>
    api<void>(`/v1/messages/templates/${encodeURIComponent(id)}`, { method: "DELETE" }),
};

export const mk = {
  conversations: (q: ConversationQuery) => ["admin", "messages", "conversations", q] as const,
  thread: (id: string) => ["admin", "messages", "thread", id] as const,
  conversation: (id: string) => ["admin", "messages", "conversation", id] as const,
  texting: ["admin", "texting"] as const,
  optOuts: (q: string) => ["admin", "texting", "opt-outs", q] as const,
  usage: (q: { from: string; to: string }) => ["admin", "texting", "usage", q] as const,
  templates: ["admin", "messages", "templates"] as const,
};

export const conversationsInfinite = (q: ConversationQuery) =>
  infiniteQueryOptions({
    queryKey: mk.conversations(q),
    queryFn: ({ pageParam, signal }) => messaging.conversations({ ...q, cursor: pageParam }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

export const threadQuery = (id: string) =>
  queryOptions({ queryKey: mk.thread(id), queryFn: () => messaging.thread(id) });
export const conversationQuery = (id: string) =>
  queryOptions({ queryKey: mk.conversation(id), queryFn: () => messaging.conversation(id) });
export const textingQuery = queryOptions({ queryKey: mk.texting, queryFn: messaging.texting });
export const optOutsQuery = (q: string) =>
  queryOptions({ queryKey: mk.optOuts(q), queryFn: () => messaging.optOuts({ q: q || undefined }) });
export const textingUsageQuery = (q: { from: string; to: string }) =>
  queryOptions({ queryKey: mk.usage(q), queryFn: () => messaging.usage(q) });
export const templatesQuery = queryOptions({
  queryKey: mk.templates,
  queryFn: messaging.templates,
  select: (d: TemplateList) => ({ ...d, items: d.items.filter((t) => t.scope === "tenant") }),
});
